<template>
	<transition name="rsb-fade">
		<div v-if="state" class="rsb" :class="`rsb--${state}`" role="status" aria-live="polite">
			<!-- Firma: la "línea de mayor" del borde superior. Recorre el toast
				mientras se recalcula, queda fija verde al actualizar y fija
				ámbar si se demora. -->
			<span class="rsb-line" aria-hidden="true"></span>
			<!-- Glifo estático: el único movimiento es la línea de arriba. -->
			<span class="rsb-glyph" aria-hidden="true">
				<svg v-if="state === 'actualizado'" viewBox="0 0 16 16" width="16" height="16" fill="none">
					<path d="M3.5 8.5l3 3 6-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
				</svg>
				<svg v-else viewBox="0 0 16 16" width="16" height="16" fill="none">
					<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5" />
					<path d="M8 4.75V8.4l2.4 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
				</svg>
			</span>
			<div class="rsb-body">
				<template v-if="state === 'recalculando'">
					<p class="rsb-title">Recalculando el reporte</p>
					<p class="rsb-detail">Incluye tus últimos cambios. Se actualizará solo en unos segundos.</p>
				</template>
				<template v-else-if="state === 'actualizado'">
					<p class="rsb-title">Reporte actualizado</p>
					<p class="rsb-detail">Ya incluye tus últimos cambios.</p>
				</template>
				<template v-else>
					<p class="rsb-title">El recálculo está tardando más de lo normal</p>
					<p class="rsb-detail">Sigues viendo la versión anterior.</p>
				</template>
			</div>
			<button v-if="state === 'demorado'" type="button" class="rsb-action" @click="refresh">Reintentar</button>
		</div>
	</transition>
</template>

<script>
import {
	REPORT_SOURCE_STALE,
	clearReportMemo,
	getReportFactStatus,
	subscribeReportSource,
} from "@/api/financialReport";
import { useImportRunPolling } from "@/composables/useImportRunPolling";

// Banner "Recalculando…" del Reporte Financiero (etapa 2 de
// docs/DECISIONES.md §19). Se enciende cuando CUALQUIER
// respuesta del tab activo llegó con X-Report-Source: stale (la empresa está en
// modo precalculado y su tabla de hechos tiene un recálculo pendiente) y se
// apaga al cambiar de tab (`resetKey`) o al refrescar, que vacía el memo y
// remonta el tab para que refetchee. Un tab hace varios requests: gana el peor
// caso (un solo dato viejo hace vieja a la vista).
//
// Mientras está prendido sondea /financial-reports/fact-status/ (la lectura
// stale ya adelantó el recálculo en el backend) y, cuando la tabla queda al
// día, refresca solo — el botón "Actualizar" queda como salida manual si el
// sondeo falla o agota sus intentos.
//
// Apagado NO sondea (decisión de Steve 2026-09-11: con el reporte al día no
// hay nada que pedir). Consulta `fact-status?pull=1` una vez al montar (toma
// la versión de datos con la que se pintó el tab) y cada vez que el usuario
// VUELVE al reporte (pestaña visible / ventana con foco): si clasificó en otra
// pestaña del navegador, el tab ya renderizado no vuelve a leer y nunca
// recibiría un stale. Dos salidas de esa consulta:
//   - `stale`: adelanta el recálculo (pull), prende el toast y sondea rápido.
//   - fresca pero con `version` mayor a la conocida (el recálculo terminó
//     entre medio, ~1 s en empresas chicas): refresca el tab ya y avisa
//     "Actualizamos el reporte" un instante.
//
// Es un toast FIJO bajo el header global (no un bloque en el flujo de la
// página): el usuario suele estar scrolleado dentro de una tabla larga cuando
// llega la respuesta stale, y un banner arriba del tab quedaba fuera de la
// vista. z-index 76: sobre el chrome (header/sidebar 60-75), bajo los modales
// (80).
export const FACT_STATUS_POLL_MS = 4000;
// ≈3 min: cubre el recálculo de las empresas grandes con holgura.
export const FACT_STATUS_MAX_ATTEMPTS = 45;
// Tiempo mínimo en pantalla antes del refresh automático: en empresas chicas
// el recálculo termina en ~1 s y sin este piso el toast era un parpadeo y el
// tab se recargaba "solo", sin que el usuario entendiera por qué.
export const MIN_VISIBLE_MS = 2500;
const FRESH_SOURCES = ["fact", "live"];

export default {
	name: "ReportStaleBanner",
	props: {
		// Cambiarla apaga el banner: el tab nuevo decide con sus propias respuestas.
		resetKey: { type: [String, Number], default: "" },
	},
	data() {
		// `stale`: hay recálculo pendiente (sondeo rápido activo). `delayed`: el
		// sondeo agotó sus intentos o falló → salida manual. `updated`: aviso
		// breve tras refrescar por versión nueva.
		return { stale: false, delayed: false, updated: false };
	},
	computed: {
		// Un solo estado visible; el botón "Reintentar" existe sólo demorado:
		// mientras se recalcula, refrescar a mano volvería a traer los mismos
		// datos viejos (la tabla aún no cambió).
		state() {
			if (this.stale) return this.delayed ? "demorado" : "recalculando";
			if (this.updated) return "actualizado";
			return null;
		},
	},
	watch: {
		resetKey() {
			this.stale = false;
			this.delayed = false;
			this.stopPolling();
		},
	},
	created() {
		// Propiedades de instancia no reactivas: la suscripción y el sondeo no
		// son estado de UI. El composable recibe `source` como `status` y
		// termina cuando deja de ser "stale"; lo detenemos a mano porque acá no
		// hay setup() que registre su onBeforeUnmount.
		this.unsubscribeSource = subscribeReportSource(this.onReportSource);
		this.poller = useImportRunPolling({
			intervalMs: FACT_STATUS_POLL_MS,
			maxAttempts: FACT_STATUS_MAX_ATTEMPTS,
			terminalStatuses: FRESH_SOURCES,
		});
		this.polling = false;
		// Piso de visibilidad: un timer propio (no Date.now) para que sea
		// determinista también bajo timers falsos en tests.
		this.minVisibleTimer = null;
		this.minVisibleDone = false;
		this.freshPending = false;
		// Versión de datos con la que se pintó el tab (null hasta la primera
		// respuesta de fact-status).
		this.knownVersion = null;
		this.updatedTimer = null;
		document.addEventListener("visibilitychange", this.onVisibilityChange);
		window.addEventListener("focus", this.checkFreshness);
		this.checkFreshness();
	},
	beforeDestroy() {
		if (this.unsubscribeSource) this.unsubscribeSource();
		this.stopPolling();
		if (this.updatedTimer !== null) clearTimeout(this.updatedTimer);
		document.removeEventListener("visibilitychange", this.onVisibilityChange);
		window.removeEventListener("focus", this.checkFreshness);
	},
	methods: {
		onReportSource(source) {
			if (source === REPORT_SOURCE_STALE) this.turnOn();
		},
		// Prende el toast, arranca el piso de visibilidad y el sondeo rápido.
		turnOn() {
			if (!this.stale) {
				this.stale = true;
				this.delayed = false;
				this.minVisibleDone = false;
				this.freshPending = false;
				this.minVisibleTimer = setTimeout(() => {
					this.minVisibleTimer = null;
					this.minVisibleDone = true;
					if (this.freshPending) this.refresh();
				}, MIN_VISIBLE_MS);
			}
			this.startPolling();
		},
		onVisibilityChange() {
			if (!document.hidden) this.checkFreshness();
		},
		// Consulta puntual (al montar y al volver al reporte), sólo con el
		// banner apagado. `pull` adelanta el recálculo en el backend si la
		// tabla está desactualizada; el sondeo rápido toma el relevo.
		checkFreshness() {
			if (this.stale || this.polling || document.hidden) return;
			getReportFactStatus({ pull: true })
				.then(({ data }) => {
					if (this.stale) return;
					if (data.source === REPORT_SOURCE_STALE) {
						this.turnOn();
						return;
					}
					const changed = this.knownVersion !== null && data.version > this.knownVersion;
					this.knownVersion = data.version;
					if (changed) this.refreshUpdated();
				})
				.catch(() => {
					// Red caída o sesión vencida: la próxima vuelta lo reintenta.
				});
		},
		// Los datos cambiaron y la tabla ya está fresca: refrescar el tab y
		// avisar un instante por qué se recargó solo.
		refreshUpdated() {
			clearReportMemo();
			this.$emit("refresh");
			this.updated = true;
			if (this.updatedTimer !== null) clearTimeout(this.updatedTimer);
			this.updatedTimer = setTimeout(() => {
				this.updatedTimer = null;
				this.updated = false;
			}, MIN_VISIBLE_MS);
		},
		startPolling() {
			// Un tab dispara varias respuestas stale seguidas: un solo sondeo.
			if (this.polling) return;
			this.polling = true;
			this.poller
				.poll(() => getReportFactStatus().then(({ data }) => ({ ...data, status: data.source })))
				.then((data) => {
					this.polling = false;
					if (data && data.version != null) this.knownVersion = data.version;
					this.scheduleRefresh();
				})
				.catch(() => {
					// Timeout del sondeo o error de red: pasa a "demorado", con
					// el botón manual como salida.
					this.polling = false;
					if (this.stale) this.delayed = true;
				});
		},
		// La tabla ya está al día: refresca ya si el toast cumplió su piso de
		// visibilidad; si no, cuando lo cumpla.
		scheduleRefresh() {
			if (!this.stale) return;
			if (this.minVisibleDone) {
				this.refresh();
			} else {
				this.freshPending = true;
			}
		},
		stopPolling() {
			this.poller.stop();
			this.polling = false;
			this.freshPending = false;
			if (this.minVisibleTimer !== null) {
				clearTimeout(this.minVisibleTimer);
				this.minVisibleTimer = null;
			}
		},
		refresh() {
			this.stopPolling();
			clearReportMemo();
			this.stale = false;
			this.delayed = false;
			this.updated = false;
			this.$emit("refresh");
		},
	},
};
</script>

<style lang="scss" scoped>
// Toast de estado del reporte. Un solo acento por estado, tomado de los
// tokens globales del reporte (:root): recalculando = el teal del reporte
// (trabajo en curso, sin alarma), actualizado = positive, demorado = el
// highlight ámbar (pide una decisión). Todo lo demás queda quieto: fondo
// blanco, hairline, Inter.
.rsb {
	--rsb-accent: var(--report-primary);
	--rsb-accent-soft: var(--report-accent-bg);
	position: fixed;
	top: calc(var(--app-chrome-top, 56px) + 12px);
	right: 24px;
	z-index: 76;
	display: flex;
	align-items: flex-start;
	gap: 12px;
	width: min(400px, calc(100vw - 32px));
	margin: 0;
	padding: 12px 14px 12px 14px;
	overflow: hidden;
	border: 1px solid var(--report-border);
	border-radius: 10px;
	background: var(--report-bg);
	color: var(--report-text);
	font-family: Inter, "Open Sans", sans-serif;
	font-size: 13px;
	line-height: 1.4;
	box-shadow: 0 1px 2px rgba(10, 13, 20, 0.04), 0 12px 32px rgba(10, 13, 20, 0.10);
}

.rsb--actualizado {
	--rsb-accent: var(--report-positive);
	--rsb-accent-soft: var(--report-positive-bg);
}

.rsb--demorado {
	--rsb-accent: var(--report-highlight-border);
	--rsb-accent-soft: var(--report-highlight-bg);
}

// Línea de mayor: 2 px en el borde superior. Recalculando, un tramo de la
// línea recorre el ancho (indeterminado); en los otros estados es fija.
.rsb-line {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 2px;
	background: var(--rsb-accent);
}

.rsb--recalculando .rsb-line {
	background: linear-gradient(90deg, transparent 0%, var(--rsb-accent) 30%, var(--rsb-accent) 70%, transparent 100%);
	background-size: 45% 100%;
	background-repeat: no-repeat;
	animation: rsb-sweep 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.rsb-glyph {
	flex: 0 0 auto;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	margin-top: 1px;
	border-radius: 50%;
	background: var(--rsb-accent-soft);
	color: var(--rsb-accent);
}

.rsb-body {
	flex: 1 1 auto;
	min-width: 0;
}

.rsb-title {
	margin: 0;
	font-size: 13px;
	font-weight: 600;
	color: var(--report-text);
	letter-spacing: -0.005em;
}

.rsb-detail {
	margin: 2px 0 0;
	font-size: 12px;
	color: var(--report-text-muted-strong);
}

.rsb-action {
	flex: 0 0 auto;
	align-self: center;
	padding: 5px 12px;
	border: 1px solid var(--report-border-soft);
	border-radius: 6px;
	background: var(--report-bg);
	color: var(--report-text-strong-2);
	font: inherit;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	transition: background var(--app-transition-fast, 150ms ease), border-color var(--app-transition-fast, 150ms ease);

	&:hover {
		background: var(--report-surface);
		border-color: var(--report-text-muted-soft);
	}

	&:focus-visible {
		outline: 2px solid var(--report-primary);
		outline-offset: 2px;
	}
}

@keyframes rsb-sweep {
	0% { background-position: -45% 0; }
	100% { background-position: 145% 0; }
}

.rsb-fade-enter-active,
.rsb-fade-leave-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.rsb-fade-enter,
.rsb-fade-leave-to {
	opacity: 0;
	transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
	.rsb--recalculando .rsb-line {
		animation: none;
		background: var(--rsb-accent);
	}

	.rsb-fade-enter-active,
	.rsb-fade-leave-active {
		transition: none;
	}
}
</style>
